import { contrastRatio, hexToRgb, isHexColor, rgbToHsl, withHsl, withLightness } from "./contrast";

/** Mínimos WCAG 2.x nível AA. */
export const WCAG_AA = { text: 4.5, largeText: 3, ui: 3 } as const;

/** Marca da 7Days, usada quando o tenant ainda não escolheu cor. */
export const DEFAULT_BRAND_COLOR = "#1A3C30";

/** Superfícies neutras da plataforma: fundo claro e `night_surface`. */
export const BRAND_SURFACES = { light: "#FFFFFF", dark: "#16201B" } as const;

const ON_BRAND_CANDIDATES = ["#FFFFFF", "#111827"] as const;
const LIGHTNESS_STEP = 0.01;

/** Fundos dos temas no matiz da marca: saturação contida para não competir com o conteúdo. */
const SURFACE_TINT = { light: { s: 0.3, l: 0.98 }, dark: { s: 0.25, l: 0.09 } } as const;

type BrandCheckId = "textOnLight" | "textOnDark" | "onBrand" | "uiOnLight" | "uiOnDark";

interface BrandCheck {
    id: BrandCheckId;
    foreground: string;
    background: string;
    ratio: number;
    minimum: number;
    passes: boolean;
}

export interface BrandPalette {
    /** Cor escolhida pelo tenant, sem ajustes. */
    base: string;
    /** Fundo de botões e faixas: a base, ou o tom mais próximo dela que aceita texto AA. */
    fill: string;
    onBrand: string;
    textOnLight: string;
    textOnDark: string;
    uiOnLight: string;
    uiOnDark: string;
    surfaces: { light: string; dark: string };
    /** True quando algum tom precisou de ser ajustado em relação à base. */
    adjusted: boolean;
    checks: BrandCheck[];
    valid: boolean;
}

function search(base: string, from: number, direction: 1 | -1, accept: (candidate: string) => boolean): { tone: string; distance: number } | null {
    for (let lightness = from; lightness >= 0 && lightness <= 1; lightness += direction * LIGHTNESS_STEP) {
        const candidate = withLightness(base, lightness);
        if (accept(candidate)) return { tone: candidate, distance: Math.abs(lightness - from) };
    }
    return null;
}

/**
 * Tom mais próximo de `base` (mesmo matiz e saturação) que atinge `minimum` sobre `surface`.
 * Escurece sobre fundo claro e clareia sobre fundo escuro; preto ou branco no limite.
 */
export function findAccessibleTone(base: string, surface: string, minimum: number): string {
    if (contrastRatio(base, surface) >= minimum) return base.toUpperCase();

    const direction = contrastRatio("#000000", surface) > contrastRatio("#FFFFFF", surface) ? -1 : 1;
    const { l } = rgbToHsl(hexToRgb(base));
    return search(base, l, direction, (candidate) => contrastRatio(candidate, surface) >= minimum)?.tone
        ?? (direction === -1 ? "#000000" : "#FFFFFF");
}

function bestOn(background: string): string {
    return ON_BRAND_CANDIDATES.reduce((best, candidate) =>
        contrastRatio(candidate, background) > contrastRatio(best, background) ? candidate : best);
}

/** Fundo de botão: a base quando algum texto passa nela; senão o tom mais próximo (claro ou escuro) que passa. */
function findFill(base: string): { fill: string; onBrand: string } {
    const onBase = bestOn(base);
    if (contrastRatio(onBase, base) >= WCAG_AA.text) return { fill: base.toUpperCase(), onBrand: onBase };

    const { l } = rgbToHsl(hexToRgb(base));
    const options = ON_BRAND_CANDIDATES.flatMap((text) => {
        const direction = contrastRatio(text, "#000000") > contrastRatio(text, "#FFFFFF") ? -1 : 1;
        const found = search(base, l, direction, (candidate) => contrastRatio(text, candidate) >= WCAG_AA.text);
        return found ? [{ ...found, text }] : [];
    });
    const closest = options.reduce((best, option) => (option.distance < best.distance ? option : best));
    return { fill: closest.tone, onBrand: closest.text };
}

function tintedSurface(base: string, theme: keyof typeof SURFACE_TINT): string {
    const { h, s } = rgbToHsl(hexToRgb(base));
    const tint = SURFACE_TINT[theme];
    return withHsl({ h, s: Math.min(s, tint.s), l: tint.l });
}

function check(id: BrandCheckId, foreground: string, background: string, minimum: number): BrandCheck {
    const ratio = contrastRatio(foreground, background);
    return { id, foreground, background, ratio, minimum, passes: ratio >= minimum };
}

/** Qualquer cor vira uma paleta AA: os tons derivados são ajustados, a base nunca é recusada. */
export function buildBrandPalette(input: string): BrandPalette {
    if (!isHexColor(input)) throw new Error(`Cor inválida: ${input}`);
    const base = input.trim().toUpperCase();

    const surfaces = { light: tintedSurface(base, "light"), dark: tintedSurface(base, "dark") };
    const textOnLight = findAccessibleTone(base, surfaces.light, WCAG_AA.text);
    const textOnDark = findAccessibleTone(base, surfaces.dark, WCAG_AA.text);
    const uiOnLight = findAccessibleTone(base, surfaces.light, WCAG_AA.ui);
    const uiOnDark = findAccessibleTone(base, surfaces.dark, WCAG_AA.ui);
    const { fill, onBrand } = findFill(base);

    const checks = [
        check("textOnLight", textOnLight, surfaces.light, WCAG_AA.text),
        check("textOnDark", textOnDark, surfaces.dark, WCAG_AA.text),
        check("onBrand", onBrand, fill, WCAG_AA.text),
        check("uiOnLight", uiOnLight, surfaces.light, WCAG_AA.ui),
        check("uiOnDark", uiOnDark, surfaces.dark, WCAG_AA.ui),
    ];

    return {
        base,
        fill,
        onBrand,
        textOnLight,
        textOnDark,
        uiOnLight,
        uiOnDark,
        surfaces,
        adjusted: [fill, textOnLight, textOnDark, uiOnLight, uiOnDark].some((tone) => tone !== base),
        checks,
        valid: checks.every((item) => item.passes),
    };
}

/** Escala 50–900 no matiz da marca, para as variáveis `--brand-*` aplicadas em runtime. */
const BRAND_SCALE_LIGHTNESS = { 50: 0.96, 100: 0.9, 200: 0.8, 300: 0.68, 400: 0.56, 500: 0.46, 600: 0.38, 700: 0.3, 800: 0.23, 900: 0.17 } as const;

export type BrandScale = Record<keyof typeof BRAND_SCALE_LIGHTNESS, string>;

export function buildBrandScale(base: string): BrandScale {
    const tone = (step: keyof typeof BRAND_SCALE_LIGHTNESS) => withLightness(base, BRAND_SCALE_LIGHTNESS[step]);
    return {
        50: tone(50), 100: tone(100), 200: tone(200), 300: tone(300), 400: tone(400),
        500: tone(500), 600: tone(600), 700: tone(700), 800: tone(800), 900: tone(900),
    };
}
